import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X, Code } from "lucide-react";

const navLinks = [
  { name: "Home", path: "/", testId: "navbar-link-home" },
  { name: "About", path: "/about", testId: "navbar-link-about" },
  { name: "Programs", path: "/programs", testId: "navbar-link-programs" },
  { name: "Contact", path: "/contact", testId: "navbar-link-contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors transition-shadow duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="w-9 h-9 rounded-xl bg-[#1677FF] flex items-center justify-center">
              <Code className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold leading-tight tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
                Maple Code
              </span>
              <span className="text-[10px] font-semibold text-muted-foreground tracking-widest uppercase leading-tight">
                Academy
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={link.testId}
                className={`text-sm font-semibold transition-colors hover:text-[#1677FF] ${
                  location.pathname === link.path
                    ? "text-[#1677FF]"
                    : "text-foreground/80"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/contact">
              <Button
                data-testid="navbar-book-trial-button"
                className="rounded-full bg-[#1677FF] hover:bg-[#0F63DA] text-white shadow-[var(--btn-shadow)] btn-lift px-6"
              >
                Book a Free Trial
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="navbar-mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] p-6">
              <div className="flex flex-col gap-6 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-${link.testId}`}
                    className={`text-lg font-semibold transition-colors ${
                      location.pathname === link.path
                        ? "text-[#1677FF]"
                        : "text-foreground/80 hover:text-[#1677FF]"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link to="/contact" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full rounded-full bg-[#1677FF] hover:bg-[#0F63DA] text-white mt-4">
                    Book a Free Trial
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
